/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Callout, DefaultButton, PrimaryButton } from '@fluentui/react';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { getId } from '@fluentui/react/lib/Utilities';
import orderBy from 'lodash.orderby';
import { observer, useLocalStore } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { BasicModal } from 'src/core/components/BasicModal';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { Page } from 'src/core/components/Page';
import { TablePagination } from 'src/core/components/TablePagination';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { routeTable } from 'src/core/utils/routeTable';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { QnaMakerFiltersBlade } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerFiltersBlade';
import { QnaMakerFilterPillContainer } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerFilterPillsContainer';
import { QnaMakerFiltersType } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerFiltersStore';
import {
    applyMetadataFilter,
    applySourcesFilter,
    applySuggestionFilter
} from 'src/qnaMaker/appShell/qnaMakerEditPage/qnaMakerFiltersUtils';
import { QnaMakerReviewSuggestionCommandBar } from 'src/qnaMaker/appShell/qnaMakeReviewSuggestionsPage/QnaMakerReviewSuggestionCommandBar';
import { QnaMakerReviewSuggestionsTable } from 'src/qnaMaker/appShell/qnaMakeReviewSuggestionsPage/QnaMakerReviewSuggestionsTable';
import { QnaMakerSuggestionsUiStore } from 'src/qnaMaker/appShell/qnaMakeReviewSuggestionsPage/QnaMakerSuggestionsUiStore';
import { QnaMakerQnaStore } from 'src/qnaMaker/core/stores/QnaMakerQnaStore';
import { useQnaPaginatedAssets } from 'src/qnaMaker/modules/hooks/useQnaPaginatedAssets';
import { useQnaPairsSuggestionActions } from 'src/qnaMaker/modules/hooks/useQnaPairsSuggestionActions';

const messages = defineMessages({
    pageTitle: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.pageTitle',
        defaultMessage: 'Review suggestions'
    },
    pageDescription: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.pageDescription',
        defaultMessage:
            'Review, accept, or reject suggested alternate phrases for questions. These suggestions come from users interacting with your bot. Only the questions with suggestions are shown.'
    },
    smartSuggestionsDescription: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.smartSuggestionsDescription',
        defaultMessage: `Review suggested alternate phrases for your questions, which you can edit, accept, or reject.  Smart suggestions are
            generated by AI based on existing answers in your project. Active learning suggestions come from user interactions with your
            knowledge base. Only questions with suggestions are shown.`
    },
    emptyStateMessage: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.emptyStateMessage',
        defaultMessage: 'There are no suggested questions yet.'
    },
    deleteQnaPairModalTitle: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.deleteQnaPairModalTitle',
        defaultMessage: 'Delete the selected question answer pair(s)'
    },
    deleteQnaPairModalBody: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.deleteQnaPairModalBody',
        defaultMessage: 'Are you sure you want to delete the selected pair(s)?'
    },
    deleteQnaMetadataModalTitle: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.deleteQnaMetadataModalTitle',
        defaultMessage: 'Delete metadata of the selected question answer pair(s)'
    },
    deleteQnaMetadataModalBody: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.deleteQnaMetadataModalBody',
        defaultMessage: 'Are you sure you want to delete the metadata of the selected pair(s)?'
    },
    deleteQnaPromptsModalTitle: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.deleteQnaPromptsModalTitle',
        defaultMessage: 'Delete follow-up prompts of the selected question answer pair(s)'
    },
    deleteQnaPromptsModalBody: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.deleteQnaPromptsModalBody',
        defaultMessage: 'Are you sure you want to delete the follow-up prompts of the selected pair(s)?'
    },
    noItemsFound: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.noItemsFound',
        defaultMessage: 'No items found.'
    },
    filtersApplied: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.filtersApplied',
        defaultMessage: 'Filtered by:'
    },
    clearAll: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.clearAll',
        defaultMessage: 'Clear all'
    },
    importModalTitle: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.importModalTitle',
        defaultMessage: 'Import Question Answer Pairs'
    },
    warningMsg: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.warningMsg',
        defaultMessage: 'You have unsaved changes. Click on Save changes in the command bar to save your recent work.'
    },
    unSavedChangesModalTitle: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.unSavedChangesModalTitle',
        defaultMessage: 'Discard un-saved changes?'
    },
    unSavedChangesModalBody: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.unSavedChangesModalBody',
        defaultMessage: 'Some changes have not been saved. If you continue, your changes will not be saved.'
    },
    unSavedChangesModalSave: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.unSavedChangesModalSave',
        defaultMessage: 'Save now'
    },
    unSavedChangesModalDiscard: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.unSavedChangesModalDiscard',
        defaultMessage: 'Discard changes'
    },
    acceptSuggestionsModalTitle: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.acceptSuggestionsModalTitle',
        defaultMessage: 'Accept all suggestions?'
    },
    acceptSuggestionsModalBody: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.acceptSuggestionsModalBody',
        defaultMessage:
            'Do you want to accept all the current suggestions? Once suggestions are accepted they are added to your knowledge base.'
    },
    acceptSelectedSuggestionsModalTitle: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.acceptSelectedSuggestionsModalTitle',
        defaultMessage: 'Accept selected suggestions?'
    },
    acceptSelectedSuggestionsModalBody: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.acceptSelectedSuggestionsModalBody',
        defaultMessage:
            'Do you want to accept all the currently selected suggestions? Once suggestions are accepted they are added to your knowledge base.'
    },
    acceptSuggestionsModalButton: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.acceptSuggestionsModalButton',
        defaultMessage: 'Accept'
    },
    rejectSuggestionsModalTitle: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.rejectSuggestionsModalTitle',
        defaultMessage: 'Reject all suggestions?'
    },
    rejectSuggestionsModalBody: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.rejectSuggestionsModalBody',
        defaultMessage:
            'Do you want to reject all the current suggestions? Once suggestions are rejected they are deleted and you will not be able to see them again.'
    },
    rejectSelectedSuggestionsModalTitle: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.rejectSelectedSuggestionsModalTitle',
        defaultMessage: 'Reject selected suggestions?'
    },
    rejectSelectedSuggestionsModalBody: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.rejectSelectedSuggestionsModalBody',
        defaultMessage:
            'Do you want to reject all the currently selected suggestions? Once suggestions are rejected they are deleted and you will not be able to see them again.'
    },
    rejectSuggestionsModalButton: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.rejectSuggestionsModalButton',
        defaultMessage: 'Reject'
    },
    smartSuggestionsFeedbackTitle: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.smartSuggestionsFeedbackTitle',
        defaultMessage: 'Are smart suggestions useful?'
    },
    feedbackText: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.feedbackText',
        defaultMessage: `Thank you for your feedback. Is there anything else you'd like to share about smart suggestions?`
    },
    feedbackPlaceholder: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.feedbackPlaceholder',
        defaultMessage: 'Enter your feedback'
    },
    sendFeedback: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.sendFeedback',
        defaultMessage: 'Send feedback'
    },
    noThanks: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.noThanks',
        defaultMessage: 'No thanks'
    },
    successfulSubmission: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.successfulSubmission',
        defaultMessage: 'Feedback submitted successfully!'
    },
    submittingFeedback: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.submittingFeedback',
        defaultMessage: 'Submitting feedback...'
    },
    positiveExperience: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.positiveExperience',
        defaultMessage: 'Positive experience'
    },
    negativeExperience: {
        id: 'qnaMaker.qnaMakerReviewSuggestionsPage.negativeExperience',
        defaultMessage: 'Negative experience'
    }
});

type ModalState = {
    isAcceptSuggestionModalHidden: boolean;
    isRejectSuggestionModalHidden: boolean;
};

/**
 * Renders the review suggestions page.
 */
export const QnaMakerReviewSuggestionsPage = observer(() => {
    const { localize } = useLocalization();

    const { rootStore, dispatcher, uiStore: portalUiStore } = useGlobalState();
    const history = useHistory();
    const uiStore = useLocalStore<QnaMakerSuggestionsUiStore>(() => new QnaMakerSuggestionsUiStore(portalUiStore));
    const { isLargeOrGreater } = useResponsiveMode();
    const [pageChangeTrigger, setPageChangeTrigger] = React.useState(getId());
    const [feedbackCalloutTarget, setFeedbackCalloutTarget] = React.useState<string>(null);
    const [feedbackText, setFeedbackText] = React.useState<string>('');
    const [modalState, updateModalState] = useUpdateState<ModalState>({
        isAcceptSuggestionModalHidden: true,
        isRejectSuggestionModalHidden: true
    });

    const {
        searchQuery,
        qnaPairsJson,
        filtersStores,
        dirtyQnaPairs,
        contextTreeMap,
        isFilterBladeOpen,
        displayedQnaPairStores,
        qnaPairsSuggestionsJson,
        isContextTreeCalculating,
        areAllSuggestionsSelected,
        areSomeSuggestionsSelected,
        qnaPairsSelectedSuggestionObj
    } = uiStore;

    const activeLearningQnaPairsJson = qnaPairsJson.filter(pair => pair.activeLearningSuggestions?.length);

    const {
        qnasJsonData,
        areQnaPairsLoaded,
        name: currentProjectName,
        isSmartSuggestionEnabled,
        sourcesStoreHouse: { items: sourceStores }
    } = rootStore.currentQnaMakerProjectStore;

    const isInEmptyState: boolean = !(!areQnaPairsLoaded || qnaPairsSuggestionsJson.length);

    const areCommandBarActionsDisabled = uiStore.savingState === 'inProgress' || (isSmartSuggestionEnabled && !areSomeSuggestionsSelected);

    const filteredQnas = React.useMemo(
        () =>
            orderBy(
                substringSearchStrategy(qnaPairsSuggestionsJson, searchQuery, ['questions', 'answer']).filter(
                    pair =>
                        applySourcesFilter(filtersStores.appliedSourcesFilters, pair) &&
                        applyMetadataFilter(filtersStores.appliedMetadataFilters, pair) &&
                        applySuggestionFilter(filtersStores.appliedSuggestionFilters, pair)
                ),
                ['id'],
                ['desc']
            ),

        [
            pageChangeTrigger,
            searchQuery,
            qnaPairsSuggestionsJson,
            filtersStores.appliedMetadataFilters,
            filtersStores.appliedQuestionFilters,
            filtersStores.appliedSourcesFilters,
            filtersStores.appliedSuggestionFilters,
            dirtyQnaPairs
        ]
    );

    const { items, currentPage, pageCount, onPageChange } = useQnaPaginatedAssets({
        pageSize: LuisConstants.QnaMaker.NumberOfDisplayedQnaPairsPerPage,
        assets: filteredQnas
    });

    const {
        savingTracker,
        acceptOrRejectSelectedQnaPairsSuggestions,
        acceptOrRejectSingleDisplayedQnaPairsSuggestions
    } = useQnaPairsSuggestionActions(uiStore);

    const handlePageChange = (pageNumber: number) => {
        onPageChange(pageNumber);
        setPageChangeTrigger(getId());
    };

    const handleOnResetFilters = () => {
        filtersStores.resetFilters();
    };

    const handleRemoveFilter = (filterType: QnaMakerFiltersType) => {
        filtersStores.resetFilters(filterType);
    };

    const handleQnaContextualNavigation = (qnaId: number) => {
        history.push(routeTable.qnaMaker.editPage(currentProjectName), { qnaId });
    };

    const handleSelectAllSuggestions = (isSelected: boolean) => {
        uiStore.changeAllSuggestionSelection(isSelected);
    };

    const feedbackSubmissionTracker = useProgressTracker({
        onComplete: () => {
            setFeedbackText('');
            setFeedbackCalloutTarget(null);
        }
    });

    const handleFeedbackSubmission = () => {
        dispatcher.dispatch('submitSmartSuggestionsFeedback', {
            feedbackText,
            tracker: feedbackSubmissionTracker,
            isPositive: feedbackCalloutTarget === 'likeFeedbackButton',
            toastData: {
                running: { title: localize(messages.submittingFeedback) },
                completed: { title: localize(messages.successfulSubmission) }
            }
        });
    };

    React.useEffect(() => {
        dispatcher.dispatch('getQnaMakerAssets', {});
    }, []);

    React.useEffect(() => {
        if (savingTracker.isFailed) {
            uiStore.setSavingState('dirty');
        }
    }, [savingTracker]);

    React.useEffect(() => {
        uiStore.setQnaPairsJson(qnasJsonData);
    }, [qnasJsonData]);

    React.useEffect(() => {
        if (!areQnaPairsLoaded) return;

        filtersStores.setMetadataFiltersItems(activeLearningQnaPairsJson);

        if (!sourceStores.find(s => s.source === 'Editorial')) {
            dispatcher.dispatch('getQnaMakerSources', { context: { forceRefresh: true } });
        }
    }, [areQnaPairsLoaded]);

    React.useEffect(() => {
        filtersStores.setSourcesFiltersItems(sourceStores);
    }, [sourceStores]);

    React.useEffect(() => {
        uiStore.setDisplayedQnaPairStores(items as QnaMakerQnaStore[]);
    }, [items]);

    return (
        <Page size="container-fluid" pageName="QnaMakerReviewSuggestionsPage" title={localize(messages.pageTitle, { currentProjectName })}>
            <Stack verticalFill tokens={{ childrenGap: 32 }}>
                <Stack tokens={{ childrenGap: 5 }}>
                    <Stack horizontal={isLargeOrGreater} horizontalAlign="space-between" verticalAlign="center">
                        <EditableTitle title={localize(messages.pageTitle, { currentProjectName })} />
                        {/*
                            @todo to be revisited later once the PMs decides the flow.
                            {isSmartSuggestionEnabled && (
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                                <LuisText>{localize(messages.smartSuggestionsFeedbackTitle)}</LuisText>
                                <IconButton
                                    id="likeFeedbackButton"
                                    ariaLabel={localize(messages.positiveExperience)}
                                    onClick={() => setFeedbackCalloutTarget('likeFeedbackButton')}
                                    iconProps={{ iconName: 'Like' }}
                                />
                                <IconButton
                                    id="dislikeFeedbackButton"
                                    ariaLabel={localize(messages.negativeExperience)}
                                    onClick={() => setFeedbackCalloutTarget('dislikeFeedbackButton')}
                                    iconProps={{ iconName: 'Dislike' }}
                                />
                            </Stack>
                        )} */}
                    </Stack>
                    <LuisText>
                        {localize(isSmartSuggestionEnabled ? messages.smartSuggestionsDescription : messages.pageDescription)}
                    </LuisText>
                </Stack>

                <Stack tokens={{ childrenGap: 22 }} verticalFill>
                    <QnaMakerReviewSuggestionCommandBar
                        searchQuery={searchQuery}
                        qnasCount={qnaPairsSuggestionsJson.length}
                        isFilterSelected={isFilterBladeOpen}
                        isLoading={!areQnaPairsLoaded}
                        optionalColumnsShown={portalUiStore.qnaMakerReviewSuggestionsOptionalColumnsVisibility}
                        onFilter={() => uiStore.toggleIsFilterBladeOpen()}
                        onChangeSearchQuery={q => uiStore.setSearchQuery(q)}
                        areEditActionsDisabled={areCommandBarActionsDisabled}
                        onAcceptOrRejectSuggestions={(isAccepted: boolean) =>
                            updateModalState(
                                isAccepted ? { isAcceptSuggestionModalHidden: false } : { isRejectSuggestionModalHidden: false }
                            )
                        }
                        onChangeOptionalColumnsShown={key =>
                            dispatcher.dispatch('toggleQnaMakerReviewSuggestionsOptionalColumnsVisibility', { key })
                        }
                    />

                    {filtersStores.allAppliedFilters.length && (
                        <Stack verticalAlign="center" horizontal tokens={{ childrenGap: 5, padding: '20px 0' }}>
                            <LuisText>{localize(messages.filtersApplied)}</LuisText>

                            <QnaMakerFilterPillContainer
                                filterItems={filtersStores.allAppliedFilters}
                                onRemoveFilter={handleRemoveFilter}
                            />
                            <Link disabled={uiStore.savingState === 'inProgress'} onClick={handleOnResetFilters}>
                                {localize(messages.clearAll)}
                            </Link>
                        </Stack>
                    )}

                    {isInEmptyState ? (
                        <EmptyStateView message={<LuisText>{localize(messages.emptyStateMessage)}</LuisText>} />
                    ) : (
                        <>
                            <QnaMakerReviewSuggestionsTable
                                sourceStores={sourceStores}
                                contextTreeMap={contextTreeMap}
                                qnaPairsJson={activeLearningQnaPairsJson}
                                items={displayedQnaPairStores}
                                qnaPairToEditId={null}
                                qnaPairsSelectedSuggestion={qnaPairsSelectedSuggestionObj}
                                onChangeSelectAllSuggestions={handleSelectAllSuggestions}
                                areAllSuggestionsSelected={areAllSuggestionsSelected}
                                onChangeSuggestionSelection={(id, suggestion, isSuggestionSelected) => {
                                    uiStore.changeSuggestionSelection(id, suggestion, isSuggestionSelected);
                                }}
                                onSubmitEditQnaPair={(id, data, isSuggestionsAccepted) => {
                                    acceptOrRejectSingleDisplayedQnaPairsSuggestions(id, data, isSuggestionsAccepted);
                                }}
                                isLoading={!areQnaPairsLoaded}
                                isContextTreeCalculating={isContextTreeCalculating}
                                areItemsFiltered={Boolean(searchQuery || filtersStores.allAppliedFilters.length)}
                                overallItemsCount={filteredQnas.length}
                                optionalColumnsVisibility={portalUiStore.qnaMakerReviewSuggestionsOptionalColumnsVisibility}
                                emptyPlaceholder={localize(messages.noItemsFound)}
                                handleQnaContextualNavigation={handleQnaContextualNavigation}
                                areQnaTableActionsDisabled={uiStore.savingState === 'inProgress'}
                            />
                            <TablePagination
                                pageCount={pageCount}
                                currentPage={currentPage}
                                onPageChange={p => handlePageChange(p.selected)}
                            />
                        </>
                    )}

                    {isFilterBladeOpen && (
                        <QnaMakerFiltersBlade
                            isOpen={isFilterBladeOpen}
                            showQuestionFiltersSection={false}
                            sourcesFilters={filtersStores.sourcesFilters}
                            metadataFilters={filtersStores.metadataFilters}
                            questionsFilters={filtersStores.questionsFilters}
                            suggestionFilters={filtersStores.suggestionFilters}
                            onDismiss={() => uiStore.toggleIsFilterBladeOpen()}
                            showSuggestionFiltersSection={isSmartSuggestionEnabled}
                            onFiltersChange={filters => filtersStores.setFilterItems(filters)}
                        />
                    )}

                    <BasicModal
                        valid
                        hidden={modalState.isAcceptSuggestionModalHidden}
                        subText={
                            isSmartSuggestionEnabled
                                ? localize(messages.acceptSelectedSuggestionsModalBody)
                                : localize(messages.acceptSuggestionsModalBody)
                        }
                        onSubmit={() => {
                            acceptOrRejectSelectedQnaPairsSuggestions(true);
                            updateModalState({ isAcceptSuggestionModalHidden: true });
                        }}
                        doneButtonText={localize(messages.acceptSuggestionsModalButton)}
                        onDismiss={() => updateModalState({ isAcceptSuggestionModalHidden: true })}
                        dialogContentProps={{
                            title: localize(messages.acceptSelectedSuggestionsModalTitle)
                        }}
                    />

                    <BasicModal
                        valid
                        hidden={modalState.isRejectSuggestionModalHidden}
                        subText={
                            isSmartSuggestionEnabled
                                ? localize(messages.rejectSelectedSuggestionsModalBody)
                                : localize(messages.rejectSuggestionsModalBody)
                        }
                        onSubmit={() => {
                            acceptOrRejectSelectedQnaPairsSuggestions(false);
                            updateModalState({ isRejectSuggestionModalHidden: true });
                        }}
                        doneButtonText={localize(messages.rejectSuggestionsModalButton)}
                        onDismiss={() => updateModalState({ isRejectSuggestionModalHidden: true })}
                        dialogContentProps={{
                            title: localize(messages.rejectSelectedSuggestionsModalTitle)
                        }}
                    />
                    {feedbackCalloutTarget && (
                        <Callout
                            role="dialog"
                            gapSpace={0}
                            styles={{
                                root: {
                                    padding: 15,
                                    maxWidth: '400px'
                                }
                            }}
                            target={`#${feedbackCalloutTarget}`}
                            onDismiss={() => setFeedbackCalloutTarget(null)}
                            setInitialFocus>
                            <Stack tokens={{ childrenGap: 25 }}>
                                <LuisText>{localize(messages.feedbackText)}</LuisText>
                                <LuisTextField
                                    multiline
                                    value={feedbackText}
                                    onChange={(_, newVal) => setFeedbackText(newVal)}
                                    placeholder={localize(messages.feedbackPlaceholder)}
                                />
                                <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 8 }}>
                                    <PrimaryButton text={localize(messages.sendFeedback)} onClick={handleFeedbackSubmission} />
                                    <DefaultButton text={localize(messages.noThanks)} onClick={handleFeedbackSubmission} />
                                </Stack>
                            </Stack>
                        </Callout>
                    )}
                </Stack>
            </Stack>
        </Page>
    );
});
